// ===========================================================================
// Shorthand counts for the static dashboards' KPI cards.
//
// The same abbreviation the real KPI widgets already show: card2kpi runs every
// figure on the card through the `numberFormat` pipe (pipe/number-format.pipe.ts),
// so 12,450 reads as "12.45K" and 3,200,000 as "3.20M". The static screens' KPI
// cards are the same kind of card at the same size, and their figures are the
// ones that grow without bound — a brand with a million files pushed "1000000"
// straight through the card's 26px value line and into its neighbour.
//
// Deliberately NOT the pipe itself, for one reason: these figures are painted by
// AnimatedNumberDirective, which owns the element's textContent and writes to it
// 60 times a second from outside Angular. A pipe cannot be applied to that, and
// interpolating one alongside the directive is exactly what the directive's own
// header warns against. So the rules live here as plain functions the directive
// calls per frame, and this file is the single place the two agree on what
// "12.45K" means.
//
// The rules match the pipe exactly, thresholds included — note that K starts at
// 10,000 rather than 1,000, so a four-digit count still reads in full:
//
//   >= 1e9  -> B      >= 1e6 -> M      >= 1e4 -> K      otherwise plain
//
// Lives at dashboard/static/ rather than inside either screen because DPM and
// DEM both use it — the same rule static-dashboards.ts sets out, and the same
// one animated-number.directive.ts beside it already follows.
//
// Donut cards do NOT use this. Their legend and ratio print exact counts, and
// stay legible by scaling their type to the longest line instead — see
// `donutFit` in each screen and .donut-inner[data-fit] in its stylesheet. The
// ring is a card about proportions, and "24/28" abbreviated to two significant
// figures would stop being checkable against the percentage printed beside it.
// ===========================================================================

/**
 * The unit one figure is printed in: what to divide by, what to suffix, and how
 * many decimals to keep.
 *
 * Chosen from the value being counted TO and then held for the whole run — see
 * shortUnitFor().
 */
export interface ShortUnit {
  divisor: number;
  suffix: string;
  decimals: number;
}

const PLAIN: ShortUnit = { divisor: 1, suffix: "", decimals: 0 };

/**
 * Pick the unit for a figure.
 *
 * Taken from the TARGET of a count rather than from whatever frame is on screen,
 * which is the whole reason this is a separate step from formatting. A tween
 * that re-chose its unit every frame would run "9,998" -> "9,999" -> "10.00K"
 * and change width three times on the way to landing; choosing once means the
 * figure keeps one unit and one decimal count for its entire run, so the card's
 * layout is settled from the first frame.
 *
 * Decimals follow the pipe's rule — a scaled value that came out whole prints
 * whole ("12K", not "12.00K"), anything else keeps two places. Read off the
 * target for the same reason as the unit: mid-count the scaled value is almost
 * always fractional, so deciding per frame would flip a whole "12K" into
 * "8.32K" and back.
 */
export function shortUnitFor(target: number): ShortUnit {
  const v = Number(target);
  if (!Number.isFinite(v)) return PLAIN;

  const abs = Math.abs(v);
  let divisor = 1;
  let suffix = "";
  if (abs >= 1e9) {
    divisor = 1e9;
    suffix = "B";
  } else if (abs >= 1e6) {
    divisor = 1e6;
    suffix = "M";
  } else if (abs >= 1e4) {
    // K at ten thousand, not one thousand — the pipe's threshold, so a count of
    // 4,500 still reads as "4,500" on both the static and the dynamic cards.
    divisor = 1e3;
    suffix = "K";
  }

  const scaled = v / divisor;
  return { divisor, suffix, decimals: scaled % 1 === 0 ? 0 : 2 };
}

/**
 * Print `n` in a unit already chosen.
 *
 * en-US rather than the browser's locale, because the pipe this mirrors groups
 * with commas unconditionally (its own formatNumberWithCommas is a comma regex).
 * A static card and a dynamic one sitting on the same screen must not disagree
 * about the separator.
 */
export function formatShort(n: number, unit: ShortUnit): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return (
    (v / unit.divisor).toLocaleString("en-US", {
      minimumFractionDigits: unit.decimals,
      maximumFractionDigits: unit.decimals,
    }) + unit.suffix
  );
}

/**
 * One-shot shorthand for a figure nothing is animating — the two steps above
 * folded together, for a value that is simply printed.
 */
export function shortCount(n: number): string {
  return formatShort(n, shortUnitFor(n));
}

/**
 * The donut cards' counterpart to abbreviating: how far a card's type has to
 * come down for its longest line to fit.
 *
 * `chars` is the length of the longest string the card is about to draw, `budget`
 * the length its layout was designed around. Returns a tier — 0 (it fits) to 3 —
 * that the stylesheet turns into a scale factor (.donut-inner[data-fit]).
 *
 * Character counts rather than measured pixels, deliberately. The alternative is
 * a ResizeObserver reading scrollWidth per change, and these figures are painted
 * by AnimatedNumberDirective 60 times a second from outside Angular — so a
 * measuring pass would run per frame, on an element that is mid-count and
 * therefore not yet the width it is going to settle at. Counting characters is
 * done once per rebuild, off the values themselves, and lands on the answer for
 * the figure that is arriving rather than the one on screen.
 *
 * The tiers are cut so that (tier scale x chars) comes back inside the budget:
 * ~1.1x over -> 0.92, ~1.3x -> 0.84, beyond that -> 0.76, which is as small as
 * this type is allowed to go before it stops being a KPI card's type at all.
 */
export function fitTier(chars: number, budget: number): number {
  if (chars <= budget) return 0;
  const over = chars / budget;
  if (over <= 1.12) return 1;
  if (over <= 1.3) return 2;
  return 3;
}
